/**
 * StoneCard custom element for displaying individual stone cards
 * 
 * @typedef {object} Refs
 *
 * @extends {Component<Refs>}
 */
class StoneCard extends HTMLElement {

  static get observedAttributes() {
    return ['stone-data', 'stone-type', 'view-mode'];
  }

  constructor() {
    super();
    this._stoneData = null;
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'view-mode' && oldValue !== newValue) {
      this.render();
    } else if (name === 'stone-data' && oldValue !== newValue) {
      // Handle stone-data attribute if needed
      this.render();
    }
  }

  get stoneData() {
    return this._stoneData;
  }

  set stoneData(data) {
    this._stoneData = data;
    this.render();
  }

  get stoneType() {
    return this.getAttribute('stone-type');
  }

  set stoneType(type) {
    this.setAttribute('stone-type', type);
  }

  get viewMode() {
    return this.getAttribute('view-mode') || 'grid';
  }

  set viewMode(mode) {
    this.setAttribute('view-mode', mode);
  }

  render() {
    if (!this._stoneData) {
      this.innerHTML = '';
      return;
    }

    const item = this._stoneData;
    const imageUrl = window.theme.stones.getStoneImage(item, this.stoneType);
    const itemName = window.theme.stones.getStoneTitle(item, this.stoneType);
    const itemSummaryDetails = window.theme.stones.getStoneSummaryDetails(item, this.stoneType);
    const price = window.theme.stones.getPrice(item, this.stoneType);
    const comparedAtPrice = window.theme.stones.getEstimatedRetailPrice(item, this.stoneType);
    const viewMode = this.viewMode;

    const slug = window.theme.stones.getStoneSlug(item, this.stoneType);
    let href = `/products/gemstone?stone=${slug}`;

    if (this.stoneType === 'color-diamond' || this.stoneType === 'diamond') {
      href = `/products/diamond?stone=${slug}`;
    } else if (this.stoneType === 'moissanite') {
      href = `/products/moissanite?stone=${slug}`;
    }

    if (viewMode === 'table') {
      // Table view is handled by stone-filters component, so this shouldn't render
      this.innerHTML = '';
      return;
    } else {
      // Grid view: vertical layout (original)
      this.innerHTML = `
        <div class="border border-primary-100 flex flex-col gap-5 relative h-full">
          <div class="aspect-square ${item.image_url ? ' p-0' : 'p-5'}">
            <a href="${href}" class="block">
              <img 
                src="${imageUrl}" 
                alt="${itemName}"
                width="400" height="400"
                class="w-full block"
                loading="lazy"
              />
            </a>
          </div>

          <div class="flex flex-col gap-4 pb-4">
            <a href="${href}" class="px-5 flex flex-col gap-3 group hover:cursor-pointer hover:no-underline">
              <div>
                <div class="text-sm block mb-1 group-hover:underline">${itemName}</div>
                <div class="text-sm block text-primary-300">${itemSummaryDetails}</div>
              </div>
              <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm leading-[14px] font-medium text-primary-500">${theme.money.format(price, true)}</span>
                  ${comparedAtPrice ? `<span class="text-sm leading-[14px] text-primary-300 line-through">${theme.money.format(comparedAtPrice, true)}</span>` : ''}
              </div>
            </a>
          </div>
        </div>
      `;
    }
  }
}

if (!customElements.get('legacy-stone-card')) {
  customElements.define('legacy-stone-card', StoneCard);
}
